import RouteNames from '@/router/names';
import store from '../../../store';
import {
	ACCOUNT_RECORDS,
	B2B_SENSITIVE_COMPANY_DETAILS,
	B2B_VIEW_COMPANY_DETAILS,
	FINANCE,
	MANAGER,
	OWNER,
	POS_BUSINESS_FINANCE,
	POS_VIEW_BUSINESS_REVIEWS,
	POS_VIEW_DISCOUNTS,
	SUPER_ADMIN,
} from '@/middlewares/permissions';

const Dashboard = {
	name: 'Dashboard',
	slug: 'dashboard',
	icon: 'chart-line',
	baboonPermissions: [POS_BUSINESS_FINANCE],
	baboonUserAccess: [SUPER_ADMIN, OWNER, FINANCE],
	routeName: RouteNames.Dashboard,
};

const Discounts = {
	name: 'Discounts',
	slug: 'discounts',
	icon: 'gift',
	baboonPermissions: [POS_VIEW_DISCOUNTS],
	baboonUserAccess: [SUPER_ADMIN, OWNER, MANAGER],
	routeName: RouteNames.DiscountList,
};

const Orders = {
	name: 'Orders',
	slug: 'orders',
	icon: 'shopping-basket',
	submenu: [
		{
			routeName: RouteNames.OrdersKanban,
			name: 'Kanban',
			slug: 'ordersKanban',
		},
		{
			routeName: RouteNames.OrdersList,
			name: 'List',
			slug: 'ordersList',
		},
		{
			routeName: RouteNames.OrdersReviews,
			name: 'Reviews',
			slug: 'ordersReviews',
			baboonPermissions: [POS_VIEW_BUSINESS_REVIEWS],
			baboonUserAccess: [SUPER_ADMIN, OWNER, MANAGER],
		},
	],
};

const Menu = {
	name: 'Menu',
	slug: 'menu',
	icon: 'menu',
	routeName: RouteNames.BusinessMenu,
	submenu: [
		{
			routeName: RouteNames.BusinessMenu,
			name: 'Menu',
			slug: 'menu',
		},
		{
			routeName: RouteNames.ToppingsList,
			name: 'Toppings',
			slug: 'toppings',
		},
		{
			routeName: RouteNames.TopProducts,
			name: 'Top products',
			slug: 'topProducts',
			baboonUserAccess: [SUPER_ADMIN, OWNER, MANAGER],
		},
	],
};

const Business = {
	name: 'My Businesses',
	slug: 'my_businesses',
	icon: 'grid',
	routeName: RouteNames.MyBusinesses,
};

const Finance = {
	name: 'Finance',
	slug: 'finance',
	icon: 'wallet',
	baboonPermissions: [POS_BUSINESS_FINANCE],
	baboonUserAccess: [SUPER_ADMIN, OWNER, FINANCE],
	submenu: [
		{
			routeName: RouteNames.FinanceOverview,
			name: 'Overview',
			slug: 'financeOverview',
		},
		{
			routeName: RouteNames.Payments,
			name: 'Payments',
			slug: 'financePayments',
		},
	],
};

const Reports = {
	name: 'Reports',
	slug: 'reports',
	icon: 'chart-line',
	baboonPermissions: [B2B_VIEW_COMPANY_DETAILS],
	baboonUserAccess: [SUPER_ADMIN, OWNER, MANAGER],
	submenu: [
		{
			routeName: RouteNames.DynamicReports,
			name: 'Dynamic Reports',
			slug: 'dynamic_reports',
		},
	],
};

const CompanyDashboard = {
	name: 'Dashboard',
	slug: 'b2bDashboard',
	icon: 'chart-line',
	baboonPermissions: [B2B_VIEW_COMPANY_DETAILS],
	baboonUserAccess: [SUPER_ADMIN],
	routeName: RouteNames.B2B_Dashboard,
};

const CompanyOrders = {
	name: 'Orders',
	slug: 'orders',
	icon: 'shopping-basket',
	submenu: [
		{
			routeName: RouteNames.CompanyOrdersList,
			name: 'CompanyOrdersList',
			slug: 'companyOrdersList',
		},
	],
};

const CompanyFinance = {
	name: 'Finance',
	slug: 'b2bFinance',
	icon: 'wallet',
	baboonPermissions: [B2B_VIEW_COMPANY_DETAILS, ACCOUNT_RECORDS],
	baboonUserAccess: [SUPER_ADMIN],
	submenu: [
		{
			routeName: RouteNames.B2B_Finance,
			name: 'Invoices',
			slug: 'invoices',
		},
		{
			routeName: RouteNames.CompanyBudgets,
			name: 'Budgets',
			slug: 'companyBudgets',
		},
		{
			routeName: RouteNames.B2B_FinanceSituation,
			name: 'Account Records',
			slug: 'accountRecords',
			baboonPermissions: [ACCOUNT_RECORDS],
			baboonUserAccess: [SUPER_ADMIN, OWNER, MANAGER],
		},
	],
};

const Company = {
	name: 'B2B Company',
	slug: 'company',
	icon: 'grid',
	baboonPermissions: [B2B_VIEW_COMPANY_DETAILS],
	baboonUserAccess: [SUPER_ADMIN],
	submenu: [
		{
			routeName: RouteNames.CompanyDetails,
			name: 'Details',
			slug: 'companyDetails',
			baboonPermissions: [B2B_SENSITIVE_COMPANY_DETAILS],
		},
		{
			routeName: RouteNames.EmployeeList,
			name: 'Employee List',
			slug: 'employeeList',
		},
	],
};

const Companies = {
	name: 'B2B Companies',
	slug: 'companies',
	icon: 'grid',
	baboonUserAccess: [SUPER_ADMIN],
	submenu: [
		{
			routeName: RouteNames.CompaniesList,
			name: 'List',
			slug: 'companiesList',
			baboonPermissions: [B2B_SENSITIVE_COMPANY_DETAILS],
		},
		{
			routeName: RouteNames.EmployeeList,
			name: 'Employee List',
			slug: 'employeeList',
		},
	],
};

const B2B_Orders = {
	name: 'B2B Orders',
	slug: 'b2bOrders',
	icon: 'calendar',
	submenu: [
		{
			routeName: RouteNames.B2B_Kanban,
			name: 'Today',
			slug: 'b2bOrdersKanban',
		},
		{
			routeName: RouteNames.B2B_WeeklyPlan,
			name: 'Weekly Plan',
			slug: 'b2bWeeklyPlan',
		},
		{
			routeName: RouteNames.B2B_OrdersList,
			name: 'List',
			slug: 'b2bOrdersList',
		},
	],
};

export const getAvailableItems = () => {
	let items = [Dashboard, Orders];
	const user = store.getters['user'];
	const isB2B = store.getters['isB2B'];
	if (!user) {
		return [];
	}
	if (user.restaurants.map((r) => r.b2bModule === 1).includes(true)) items.push(B2B_Orders);
	items.push(Discounts);
	if (!user.restaurants || user.restaurants.length === 1) {
		items.push(Menu);
	} else {
		items.push(Business);
	}
	items.push(Finance);
	items.push(Reports);
	if (isB2B) {
		if (user.restaurants?.length === 1) {
			items = [CompanyDashboard, CompanyOrders, Company, CompanyFinance];
		} else {
			items = [CompanyDashboard, CompanyOrders, Companies, CompanyFinance];
		}
	}

	return items;
};
